/**
 * Request an idle callback or fallback to setTimeout.
 *
 * 用于把非紧急任务延迟到浏览器空闲时执行；当浏览器不支持
 * `window.requestIdleCallback` 时，会自动降级为 `setTimeout`。
 *
 * @example
 * import { requestIdleCallback } from '@jxs/utilities';
 *
 * requestIdleCallback(() => {
 *   // 执行非首屏关键任务
 * });
 *
 * @returns {function} The requestIdleCallback function.
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

/**
 * 延迟执行回调，并在等待时间内重复触发时重置计时。
 *
 * 适用于 resize、scroll、input 等高频事件，避免回调被连续触发。
 * 返回的函数带有 `cancel` 方法，可在组件卸载时清除尚未执行的回调。
 *
 * @example
 * import { debounce } from '@jxs/utilities';
 *
 * const onResize = debounce(() => {
 *   // resize 停止 100ms 后执行
 * }, 100);
 *
 * window.addEventListener('resize', onResize);
 * window.removeEventListener('resize', onResize);
 * onResize.cancel();
 *
 * @template {unknown[]} T
 * @param {(...args: T) => void} callback 要延迟执行的回调。
 * @param {number} wait 等待时间，单位为毫秒。
 * @returns {((...args: T) => void) & { cancel: () => void }} 包含 cancel 方法的防抖函数。
 */
export function debounce(callback, wait) {
  /** @type {number | null} */
  let timer = null;

  /** @type {((...args: T) => void) & { cancel: () => void }} */
  const debounced = (...args) => {
    if (timer !== null) {
      window.clearTimeout(timer);
    }

    timer = window.setTimeout(() => callback(...args), wait);
  };

  debounced.cancel = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  return debounced;
}

/**
 * 按固定时间间隔执行回调，限制高频事件的触发频率。
 *
 * 适用于 scroll、resize、mousemove 等高频事件。首次调用会立即执行，
 * 等待时间内重复触发时会记录最后一次参数，并在等待结束后补执行一次。
 * 返回的函数带有 `cancel` 方法，可在组件卸载时清除尚未执行的回调。
 *
 * @example
 * import { throttle } from '@jxs/utilities';
 *
 * const onScroll = throttle(() => {
 *   // 最多每 100ms 执行一次
 * }, 100);
 *
 * window.addEventListener('scroll', onScroll);
 * window.removeEventListener('scroll', onScroll);
 * onScroll.cancel();
 *
 * @template {unknown[]} T
 * @param {(...args: T) => void} callback 要节流执行的回调。
 * @param {number} wait 间隔时间，单位为毫秒。
 * @returns {((...args: T) => void) & { cancel: () => void }} 包含 cancel 方法的节流函数。
 */
export function throttle(callback, wait) {
  /** @type {number | null} */
  let timer = null;
  /** @type {T | null} */
  let lastArgs = null;

  /** @type {((...args: T) => void) & { cancel: () => void }} */
  const throttled = (...args) => {
    if (timer === null) {
      callback(...args);

      timer = window.setTimeout(() => {
        timer = null;

        if (lastArgs !== null) {
          const trailingArgs = lastArgs;
          lastArgs = null;
          throttled(...trailingArgs);
        }
      }, wait);

      return;
    }

    lastArgs = args;
  };

  throttled.cancel = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }

    lastArgs = null;
  };

  return throttled;
}

/**
 * 空函数，适合作为可调用返回值或默认回调使用。
 *
 * @returns {void}
 */
export function noop() {}

/**
 * 将数值限制在指定范围内。
 *
 * @param {*} value 待转换并限制范围的值。
 * @param {number} min 最小值。
 * @param {number} max 最大值。
 * @param {number} [defaultValue=min] value 无法转换为有效数字时使用的默认值。
 * @returns {number}
 */
export function clamp(value, min, max, defaultValue = min) {
  const number = Number(value);

  if (Number.isNaN(number)) {
    return defaultValue;
  }

  return Math.min(Math.max(number, min), max);
}

/**
 * 解析 DOM 元素。
 *
 * 支持直接传入 Element，也支持传入 CSS 选择器字符串。
 * 当传入字符串时，会在 root 范围内查询第一个匹配元素。
 *
 * @param {Element|string|null|undefined} element DOM 元素或 CSS 选择器。
 * @param {ParentNode} [root=document] 查询选择器时使用的根节点。
 * @returns {Element|null}
 */
export function resolveElement(element, root = document) {
  if (typeof element === 'string') {
    return root.querySelector(element);
  }

  if (element instanceof Element) {
    return element;
  }

  return null;
}

/**
 * 解析 DOM 元素列表。
 *
 * 支持单个 Element、CSS 选择器字符串、Element / 选择器数组、NodeList 和 HTMLCollection。
 * 当传入字符串时，会在 root 范围内查询所有匹配元素。
 *
 * @param {Element|string|Array<Element|string>|NodeListOf<Element>|HTMLCollection|null|undefined} elements DOM 元素、CSS 选择器或元素集合。
 * @param {ParentNode} [root=document] 查询选择器时使用的根节点。
 * @returns {Element[]}
 */
export function resolveElements(elements, root = document) {
  if (!elements) return [];

  if (typeof elements === 'string') {
    return Array.from(root.querySelectorAll(elements));
  }

  if (elements instanceof Element) {
    return [elements];
  }

  /** @type {Array<Element|string>} */
  const elementList = Array.from(elements);
  /** @type {Element[]} */
  const resolvedElements = [];

  for (const element of elementList) {
    const resolvedElement = resolveElement(element, root);

    if (resolvedElement) {
      resolvedElements.push(resolvedElement);
    }
  }

  return resolvedElements;
}

/**
 * 获取元素在当前视口中的垂直可见比例。
 *
 * 返回值以元素自身高度为基准：
 * - 0 表示元素完全不可见。
 * - 1 表示元素高度范围完全处于视口内。
 * - 0.5 表示元素有一半高度处于视口内。
 *
 * @param {Element|null|undefined} element 需要计算的 DOM 元素。
 * @returns {number} 元素当前的可见比例，范围为 0~1。
 */
export function getElementViewportRatio(element) {
  if (!(element instanceof Element)) {
    return 0;
  }

  const rect = element.getBoundingClientRect();
  const height = rect.height;

  if (height <= 0) {
    return 0;
  }

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, viewportHeight);
  const visibleHeight = Math.max(visibleBottom - visibleTop, 0);

  return clamp(visibleHeight / height, 0, 1, 0);
}

/**
 * 判断字符串是否为空。
 * @param {string|null|undefined} str 待判断的字符串。
 * @returns {boolean}
 */
export function isBlank(str) {
  return str == null || str == undefined || str === '' || str.trim() === '';
}


/**
 * Swiper slideChange 事件管理器。
 * 
 * 结合slideChangeTransitionStart和slideChangeTransitionEnd两个事件，通知外部监听者，
 * 可以得到：
 * swiper: 实例
 * prevIndex：上一个slide的业务下标
 * destIndex 下一个slide（将被active）的业务下标
 * activeIndex 数组下标，可用于获取swiper.slides[activeIndex]
 *
 * // 组件卸载时清理
 * swiperSlideChange.destroy();
 */
export class JxsSwiperSlideChange {
  /** @type {any} */
  #swiper;

  /** @type {Function[]} */
  #listeners = [];

  #prevIndex = 0;

  /**
   * @param {any} swiper Swiper 实例
   */
  constructor(swiper) {
    this.#swiper = swiper;
    this.#bindEvents();
  }

  #bindEvents() {
    this.#swiper.on('slideChangeTransitionStart', this.#onSlideChangeTransitionStart);
    this.#swiper.on('slideChangeTransitionEnd', this.#onSlideChangeTransitionEnd);
  }

  #onSlideChangeTransitionStart = () => {
    this.#prevIndex = this.#swiper.realIndex;
  };

  #onSlideChangeTransitionEnd = () => {
    const prevIndex = this.#prevIndex;
    const destIndex = this.#swiper.realIndex;
    const activeIndex = this.#swiper.activeIndex;
    this.#listeners.forEach(callback => {
      try {
        callback(this.#swiper, prevIndex, destIndex, activeIndex);
      } catch (error) {
        console.warn('Swiper slide change listener error:', error);
      }
    });
  };

  /**
   * 添加 slideChange 监听器。
   *
   * @param {(swiper: any, prevIndex: number, destIndex: number, activeIndex: number) => void} callback
   * @returns {this}
   */
  addListener(callback) {
    if (typeof callback === 'function') {
      this.#listeners.push(callback);
    }
    return this;
  }

  /**
   * 移除指定监听器。
   *
   * @param {(swiper: any, prevIndex: number, destIndex: number, activeIndex: number) => void} callback
   * @returns {this}
   */
  removeListener(callback) {
    const index = this.#listeners.indexOf(callback);
    if (index > -1) {
      this.#listeners.splice(index, 1);
    }
    return this;
  }

  /**
   * 移除所有监听器并解绑 Swiper 事件。
   */
  destroy() {
    this.#listeners = [];
  }
}